import { apiRequest } from "./client";
import type {
  AdminUser,
  AuditLogEntry,
  Device,
  DeviceGroup,
  Report,
  ReportCategory,
  DraftSnapshot, DraftSnapshotDetail, ReportVersionDetail, VersionDiff,
  AiChatRequest,
  AiChatResponse,
  PresenceBeat,
  PresenceInfo,
  ReportDraft,
  ReportPermissionEntry,
  ReportPublication,
  ReportVersion,
  SystemHealth,
  Tenant,
} from "../types";
import type { CrossFilterValue } from "../runtime/CrossFilterContext";

export const adminApi = {
  listTenants: () => apiRequest<Tenant[]>("/api/v1/admin/tenants"),
  createTenant: (payload: { tenant_id: string; display_name: string; primary_domain: string }) =>
    apiRequest<Tenant>("/api/v1/admin/tenants", { method: "POST", body: payload }),
  setTenantActive: (id: string, active: boolean) =>
    apiRequest<Tenant>(`/api/v1/admin/tenants/${id}/${active ? "enable" : "disable"}`, {
      method: "POST",
    }),

  listUsers: () => apiRequest<AdminUser[]>("/api/v1/admin/users"),
  setUserActive: (id: string, isActive: boolean) =>
    apiRequest<AdminUser>(`/api/v1/admin/users/${id}`, {
      method: "PUT",
      body: { is_active: isActive },
    }),
  assignRole: (id: string, role: string) =>
    apiRequest<AdminUser>(`/api/v1/admin/users/${id}/roles`, { method: "POST", body: { role } }),
  removeRole: (id: string, role: string) =>
    apiRequest<AdminUser>(`/api/v1/admin/users/${id}/roles/${role}`, { method: "DELETE" }),

  listRoles: () => apiRequest<string[]>("/api/v1/admin/roles"),

  listDeviceGroups: () => apiRequest<DeviceGroup[]>("/api/v1/admin/device-groups"),
  createDeviceGroup: (payload: { name: string; description?: string }) =>
    apiRequest<DeviceGroup>("/api/v1/admin/device-groups", { method: "POST", body: payload }),

  deleteDeviceGroup: (id: string) =>
    apiRequest<void>(`/api/v1/admin/device-groups/${id}`, { method: "DELETE" }),

  listDevices: () => apiRequest<Device[]>("/api/v1/admin/devices"),
  /** New screen: name + group. Screen signing in again: both optional (kept).
   * replace_device_id: the TV takes over that screen's name, group and assignments. */
  activateDevice: (payload: { code: string; name?: string; device_group_id?: string; description?: string; replace_device_id?: string }) =>
    apiRequest<Device>("/api/v1/admin/devices/activate", { method: "POST", body: payload }),
  lookupActivation: (code: string) =>
    apiRequest<ActivationLookup>("/api/v1/admin/devices/activation-lookup", { method: "POST", body: { code } }),
  updateDevice: (id: string, payload: { name?: string; description?: string; device_group_id?: string }) =>
    apiRequest<Device>(`/api/v1/admin/devices/${id}`, { method: "PUT", body: payload }),
  deleteDevice: (id: string) => apiRequest<void>(`/api/v1/admin/devices/${id}`, { method: "DELETE" }),
  bulkDeleteDevices: (ids: string[]) =>
    apiRequest<{ deleted: string[]; not_found: string[] }>("/api/v1/admin/devices/bulk-delete", { method: "POST", body: { ids } }),
  enableDevice: (id: string) =>
    apiRequest<Device>(`/api/v1/admin/devices/${id}/enable`, { method: "POST" }),
  disableDevice: (id: string) =>
    apiRequest<Device>(`/api/v1/admin/devices/${id}/disable`, { method: "POST" }),
  revokeDevice: (id: string) =>
    apiRequest<Device>(`/api/v1/admin/devices/${id}/revoke`, { method: "POST" }),
  rotateDeviceToken: (id: string) =>
    apiRequest<Device>(`/api/v1/admin/devices/${id}/rotate-token`, { method: "POST" }),

  listAuditLogs: () => apiRequest<AuditLogEntry[]>("/api/v1/admin/audit"),

  systemHealth: () => apiRequest<SystemHealth>("/api/v1/admin/system-health"),

  listReports: () => apiRequest<Report[]>("/api/v1/admin/reports"),
  createReport: (payload: { name: string; description?: string; category_id?: string | null }) =>
    apiRequest<Report>("/api/v1/admin/reports", { method: "POST", body: payload }),
  getReport: (id: string) => apiRequest<Report>(`/api/v1/admin/reports/${id}`),
  /** category_id: null moves the report to Uncategorized; leave it out to keep the current one. */
  updateReport: (id: string, payload: { name?: string; description?: string; category_id?: string | null }) =>
    apiRequest<Report>(`/api/v1/admin/reports/${id}`, { method: "PUT", body: payload }),
  deleteReport: (id: string) =>
    apiRequest<void>(`/api/v1/admin/reports/${id}`, { method: "DELETE" }),
  archiveReport: (id: string) =>
    apiRequest<Report>(`/api/v1/admin/reports/${id}/archive`, { method: "POST" }),
  restoreReport: (id: string) =>
    apiRequest<Report>(`/api/v1/admin/reports/${id}/restore`, { method: "POST" }),
  duplicateReport: (id: string) =>
    apiRequest<Report>(`/api/v1/admin/reports/${id}/duplicate`, { method: "POST" }),

  /** Company categories (MOP, MOS, ...), sorted by sort_order then name. */
  listReportCategories: () => apiRequest<ReportCategory[]>("/api/v1/admin/report-categories"),
  /** 409 when a category with that name already exists. */
  createReportCategory: (payload: { name: string; sort_order?: number }) =>
    apiRequest<ReportCategory>("/api/v1/admin/report-categories", { method: "POST", body: payload }),
  updateReportCategory: (id: string, payload: { name?: string; sort_order?: number }) =>
    apiRequest<ReportCategory>(`/api/v1/admin/report-categories/${id}`, { method: "PUT", body: payload }),
  /** Its reports become uncategorized; the reports themselves are kept. */
  deleteReportCategory: (id: string) =>
    apiRequest<void>(`/api/v1/admin/report-categories/${id}`, { method: "DELETE" }),

  getDraft: (id: string) => apiRequest<ReportDraft>(`/api/v1/admin/reports/${id}/draft`),
  getPreviewData: (id: string, filters?: CrossFilterValue[] | null) => {
    const params = filters && filters.length > 0 ? `?filters=${encodeURIComponent(JSON.stringify(filters))}` : "";
    return apiRequest<Record<string, unknown>>(`/api/v1/admin/reports/${id}/preview-data${params}`);
  },
  /** Draft-mode counterpart to viewerApi.getDetailDrawerData -- same request
   * shape, but resolved against this report's current draft instead of a
   * published version, so the Detail Drawer can be exercised in the
   * Designer without publishing first. */
  getDetailDrawerData: (
    id: string,
    body: { datasetId: string; dimensions: string[]; filters: Array<{ field: string; value: unknown }>; limit?: number },
  ) =>
    apiRequest<{ data: { columns: string[]; rows: unknown[][] } }>(
      `/api/v1/admin/reports/${id}/widgets/detail`,
      { method: "POST", body },
    ),
  saveDraft: (id: string, payload: { definition: Record<string, unknown>; expectedRevision: number }) =>
    apiRequest<ReportDraft>(`/api/v1/admin/reports/${id}/draft`, { method: "PUT", body: payload }),
  publishReport: (id: string) =>
    apiRequest<ReportVersion>(`/api/v1/admin/reports/${id}/publish`, { method: "POST" }),
  listVersions: (id: string) => apiRequest<ReportVersion[]>(`/api/v1/admin/reports/${id}/versions`),
  listDraftSnapshots: (id: string) => apiRequest<DraftSnapshot[]>(`/api/v1/admin/reports/${id}/draft-snapshots`),
  getVersionDetail: (id: string, versionId: string) => apiRequest<ReportVersionDetail>(`/api/v1/admin/reports/${id}/versions/${versionId}`),
  getVersionDiff: (id: string, versionId: string, against = "previous") => apiRequest<VersionDiff>(`/api/v1/admin/reports/${id}/versions/${versionId}/diff?against=${encodeURIComponent(against)}`),
  getDraftSnapshotDetail: (id: string, snapshotId: string) => apiRequest<DraftSnapshotDetail>(`/api/v1/admin/reports/${id}/draft-snapshots/${snapshotId}`),
  getDraftSnapshotDiff: (id: string, snapshotId: string, against = "previous") => apiRequest<VersionDiff>(`/api/v1/admin/reports/${id}/draft-snapshots/${snapshotId}/diff?against=${encodeURIComponent(against)}`),
  /** Designer "Ask AI": takes 20-60 s, so no timeout; pass a signal to cancel. Never saves the draft. */
  aiChat: (id: string, payload: AiChatRequest, signal?: AbortSignal) =>
    apiRequest<AiChatResponse>(`/api/v1/admin/reports/${id}/ai/chat`, { method: "POST", body: payload, signal }),
  presence: (id: string, beat: PresenceBeat) =>
    apiRequest<PresenceInfo>(`/api/v1/admin/reports/${id}/presence`, { method: "POST", body: beat }),
  leavePresence: (id: string, clientId: string) =>
    apiRequest<void>(`/api/v1/admin/reports/${id}/presence?client_id=${encodeURIComponent(clientId)}`, { method: "DELETE" }),
  saveDraftSnapshot: (id: string, label?: string) =>
    apiRequest<DraftSnapshot>(`/api/v1/admin/reports/${id}/draft-snapshots`, { method: "POST", body: { label: label || null } }),
  restoreDraftSnapshot: (id: string, snapshotId: string) =>
    apiRequest<ReportDraft>(`/api/v1/admin/reports/${id}/draft-snapshots/${snapshotId}/restore`, { method: "POST" }),
  validateDraft: (id: string) => apiRequest<DraftCheck>(`/api/v1/admin/reports/${id}/validate`, { method: "POST" }),
  getPublishedDefinition: (id: string) =>
    apiRequest<{ version: number; publishedAt: string; definition: Record<string, unknown> }>(`/api/v1/admin/reports/${id}/published-definition`),
  rollbackReport: (id: string, payload: { version_id: string; reason?: string }) =>
    apiRequest<ReportPublication>(`/api/v1/admin/reports/${id}/rollback`, {
      method: "POST",
      body: payload,
    }),

  listReportPermissions: (id: string) =>
    apiRequest<ReportPermissionEntry[]>(`/api/v1/admin/reports/${id}/permissions`),
  updateReportPermissions: (id: string, permissions: ReportPermissionEntry[]) =>
    apiRequest<ReportPermissionEntry[]>(`/api/v1/admin/reports/${id}/permissions`, {
      method: "PUT",
      body: { permissions },
    }),
  removeReportPermission: (id: string, principalType: ReportPermissionEntry["principal_type"], principalId: string) =>
    apiRequest<void>(`/api/v1/admin/reports/${id}/permissions/${principalType}/${principalId}`, { method: "DELETE" }),
  /** Replace-set: exactly these groups and single screens show the report (unticked ones are removed). */
  setReportScreens: (id: string, payload: { device_group_ids: string[]; device_ids: string[] }) =>
    apiRequest<ScreenTargets>(`/api/v1/admin/reports/${id}/screens`, { method: "PUT", body: payload }),
};

export interface ActivationLookup {
  /** "new" = a TV joining for the first time; "reconnect" = an existing screen signing in again. */
  kind: "new" | "reconnect";
  device: Device;
  suggestions: Array<{ device_id: string; reason: string }>;
}

export interface ScreenTargets {
  device_group_ids: string[];
  device_ids: string[];
  screen_count: number;
  screen_names: string[];
  laptop_count?: number;
  added: number;
  removed: number;
}

export interface DraftCheck {
  valid: boolean;
  errors: string[];
  draftRevision: number;
  summary: { pages: number; widgets: number; unboundWidgets: Array<{ page: string; widgetId: string; title: string }> };
  changes: {
    firstPublish: boolean;
    pagesAdded: string[];
    pagesRemoved: string[];
    pagesRenamed: string[];
    widgetsAdded: number;
    widgetsRemoved: number;
    widgetsChanged: number;
    settingsChanged: string[];
  };
}
